import React, { useState } from 'react'
import { Button, Form, Input, Modal, Popconfirm, Select, Spin, message } from 'antd'
import { EyeOutlined, ExperimentOutlined, SendOutlined } from '@ant-design/icons'
import { hookService } from 'services/hookService'
import { userService } from 'services'

/**
 * Webhook "Riscos do planejamento" (tipo 15): só pode ser enviado por e-mail (disparo 3), e o disparo por
 * e-mail só existe para esse tipo. A própria API manda um resumo todo dia às 7h e às 18h com os riscos
 * (atrasa a entrega, atrasada na programação, falta de material).
 * No e-mail, o campo address guarda os destinatários separados por ";".
 */
export const PLANNING_RISKS_TYPE = 15
export const MAIL_TRIGGER = 3
export const VALIDATION_TYPES = [10, 11, 13, 14]

export const isPlanningRisks = (hookType) => hookType === PLANNING_RISKS_TYPE

const EMAIL = /^[^\s@;,]+@[^\s@;,]+\.[^\s@;,]+$/

// e-mails dos usuários do PlantScanner, carregados uma vez por tela aberta
let userEmailsRequest = null
const loadUserEmails = () => {
  if (!userEmailsRequest)
    userEmailsRequest = userService
      .getAll()
      .then((users) =>
        Array.from(
          new Set(
            (users || [])
              .filter((user) => user && user.email && !user.deactivated)
              .map((user) => String(user.email).trim().toLowerCase())
          )
        ).sort()
      )
      .catch(() => {
        userEmailsRequest = null
        return []
      })
  return userEmailsRequest
}

/**
 * Campo de destinatários: sugere os e-mails dos usuários cadastrados enquanto digita e aceita qualquer
 * outro e-mail (digite e aperte Enter, ou separe com ; , ou espaço). Também dá para colar uma lista.
 */
export const RecipientsSelect = ({ t, value, onChange, ...rest }) => {
  const [options, setOptions] = useState([])
  React.useEffect(() => {
    let active = true
    loadUserEmails().then((emails) => active && setOptions(emails))
    return () => {
      active = false
    }
  }, [])
  const selected = (value || []).map((email) => String(email).toLowerCase())
  return (
    <Select
      mode="tags"
      value={value}
      onChange={(next) =>
        onChange &&
        onChange(
          Array.from(new Set((next || []).map((email) => String(email).trim().toLowerCase())))
        )
      }
      tokenSeparators={[';', ',', ' ']}
      style={{ width: '100%' }}
      placeholder={t(
        'hooks.planningRisks.recipientsPlaceholder',
        'Digite para buscar um usuário ou um e-mail e aperte Enter'
      )}
      filterOption={(input, option) =>
        String(option.value).toLowerCase().includes(String(input).toLowerCase())
      }
      notFoundContent={null}
      {...rest}
    >
      {options
        .filter((email) => !selected.includes(email))
        .map((email) => (
          <Select.Option key={email} value={email}>
            {email}
          </Select.Option>
        ))}
    </Select>
  )
}

export const splitRecipients = (address) =>
  String(address || '')
    .split(/[;,\s]+/)
    .map((part) => part.trim())
    .filter(Boolean)

/** Valores do formulário a partir do webhook salvo. */
export const toFormValues = (hook) =>
  hook && isPlanningRisks(hook.hookType)
    ? { recipients: splitRecipients(hook.address) }
    : { recipients: [] }

/** O que vai para a API: no e-mail, os destinatários viram o address. */
export const toPayload = (values) => {
  const { recipients, ...rest } = values
  if (!isPlanningRisks(values.hookType)) return rest
  return {
    ...rest,
    hookTrigger: MAIL_TRIGGER,
    authType: 0,
    authToken: null,
    address: (recipients || []).map((email) => email.trim().toLowerCase()).join('; '),
  }
}

/** Ao trocar o tipo: riscos do planejamento vai sempre por e-mail; os outros não podem usar e-mail. */
export const onHookTypeChange = (form, value) => {
  if (isPlanningRisks(value)) {
    form.setFieldsValue({ hookTrigger: MAIL_TRIGGER, authType: 0 })
    return
  }
  if (form.getFieldValue('hookTrigger') === MAIL_TRIGGER) form.setFieldValue('hookTrigger', 0)
}

export const triggerLabel = (t, trigger) => {
  switch (trigger) {
    case 0:
      return t('hooks.triggerType.post')
    case 1:
      return t('hooks.triggerType.azureQueue')
    case 2:
      return t('hooks.triggerType.sms')
    case MAIL_TRIGGER:
      return t('hooks.triggerType.mail', 'E-mail')
    default:
      return trigger
  }
}

/** URL do webhook ou, em riscos do planejamento, os e-mails que recebem. */
export const AddressField = ({ t }) => (
  <Form.Item noStyle shouldUpdate={(prev, current) => prev.hookType !== current.hookType}>
    {({ getFieldValue }) =>
      isPlanningRisks(getFieldValue('hookType')) ? (
        <Form.Item
          name="recipients"
          label={t('hooks.planningRisks.recipients', 'E-mails que recebem')}
          extra={t(
            'hooks.planningRisks.recipientsHelp',
            'Digite para buscar os usuários cadastrados ou escreva qualquer e-mail e aperte Enter (pode colar uma lista separada por ;). Resumo todo dia às 7h e às 18h, das estações escolhidas abaixo.'
          )}
          rules={[
            {
              validator: (_, value) => {
                const list = value || []
                if (list.length === 0)
                  return Promise.reject(
                    new Error(
                      t('hooks.planningRisks.recipientsRequired', 'Informe ao menos um e-mail')
                    )
                  )
                const invalid = list.filter((email) => !EMAIL.test(String(email).trim()))
                if (invalid.length)
                  return Promise.reject(
                    new Error(
                      t('hooks.planningRisks.recipientsInvalid', {
                        defaultValue: 'E-mail inválido: {{emails}}',
                        emails: invalid.join(', '),
                        interpolation: { escapeValue: false },
                      })
                    )
                  )
                return Promise.resolve()
              },
            },
          ]}
        >
          <RecipientsSelect t={t} />
        </Form.Item>
      ) : (
        <Form.Item
          name="address"
          label={t('hooks.modal.form.name')}
          rules={[{ required: true, message: t('hooks.nameRequired') }]}
        >
          <Input placeholder={t('hooks.modal.form.namePlaceholder')} />
        </Form.Item>
      )
    }
  </Form.Item>
)

/** Opções de disparo: e-mail só para riscos do planejamento. */
export const triggerOptions = (t, hookType) => {
  const values = isPlanningRisks(hookType) ? [MAIL_TRIGGER] : [0, 1]
  return values.map((value) => (
    <Select.Option key={value} value={value}>
      {triggerLabel(t, value)}
    </Select.Option>
  ))
}

const currentUserEmail = () => {
  try {
    return window.localStorage.getItem('user-email') || ''
  } catch (error) {
    return ''
  }
}

/**
 * Testar envio (só no webhook de riscos do planejamento): manda o e-mail agora, com a configuração que está
 * no formulário (dá para testar antes de salvar), para os e-mails escolhidos. Vai com "[Teste]" no assunto,
 * não fica nos envios do webhook e não conta como resumo nem como aviso.
 */
const TestSendModal = ({ t, form, open, onClose }) => {
  const [to, setTo] = useState([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)

  React.useEffect(() => {
    if (!open) return
    const recipients = form.getFieldValue('recipients') || []
    const me = currentUserEmail()
    setTo(recipients.length ? recipients : me ? [me] : [])
    setError(null)
  }, [open, form])

  const send = async () => {
    const invalid = to.filter((email) => !EMAIL.test(String(email).trim()))
    if (to.length === 0 || invalid.length) {
      setError(
        invalid.length
          ? t('hooks.planningRisks.recipientsInvalid', {
              defaultValue: 'E-mail inválido: {{emails}}',
              emails: invalid.join(', '),
              interpolation: { escapeValue: false },
            })
          : t('hooks.planningRisks.recipientsRequired', 'Informe ao menos um e-mail')
      )
      return
    }
    setSending(true)
    setError(null)
    try {
      const values = form.getFieldsValue(['description', 'allWorkStations', 'workStationsIds'])
      const { data } = await hookService.testPlanningRisks({
        description: values.description,
        allWorkStations: !!values.allWorkStations,
        workStationsIds: values.allWorkStations ? [] : values.workStationsIds || [],
        recipients: to.map((email) => email.trim().toLowerCase()),
      })
      if (data && data.sended) {
        message.success(
          data.resultMessage || t('hooks.planningRisks.testSent', 'E-mail de teste enviado')
        )
        onClose()
      } else {
        setError(
          (data && data.resultMessage) ||
            t('hooks.planningRisks.sendError', 'Não foi possível enviar.')
        )
      }
    } catch (err) {
      const data = err && err.response && err.response.data
      setError(
        (data && data.resultMessage) ||
          t('hooks.planningRisks.sendError', 'Não foi possível enviar.')
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('hooks.planningRisks.testTitle', 'Testar envio do e-mail de riscos')}
      okText={t('hooks.planningRisks.testSend', 'Enviar teste')}
      cancelText={t('hooks.cancel')}
      onOk={send}
      confirmLoading={sending}
    >
      <p style={{ fontSize: 13, color: '#595959' }}>
        {t(
          'hooks.planningRisks.testHelp',
          'Manda agora o e-mail com os riscos de hoje das estações escolhidas no formulário, mesmo antes de salvar. Vai com "[Teste]" no assunto e não conta como o resumo do dia nem como aviso.'
        )}
      </p>
      <div style={{ marginBottom: 6, fontWeight: 500 }}>
        {t('hooks.planningRisks.testTo', 'Enviar para')}
      </div>
      <RecipientsSelect t={t} value={to} onChange={setTo} />
      {error && <div style={{ marginTop: 8, color: '#cf1322', fontSize: 13 }}>{error}</div>}
    </Modal>
  )
}

/** Botão "Testar envio" do formulário: só aparece quando o tipo é Riscos do planejamento. */
export const TestSendButton = ({ t, form }) => {
  const [open, setOpen] = useState(false)
  return (
    <Form.Item noStyle shouldUpdate={(prev, current) => prev.hookType !== current.hookType}>
      {({ getFieldValue }) =>
        isPlanningRisks(getFieldValue('hookType')) ? (
          <>
            <Button icon={<ExperimentOutlined />} onClick={() => setOpen(true)}>
              {t('hooks.planningRisks.test', 'Testar envio')}
            </Button>
            <TestSendModal t={t} form={form} open={open} onClose={() => setOpen(false)} />
          </>
        ) : null
      }
    </Form.Item>
  )
}

/** Ver prévia e Enviar agora, nos detalhes do webhook. */
export const PlanningRiskActions = ({ t, hookId, onSent }) => {
  const [preview, setPreview] = useState(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [sending, setSending] = useState(false)

  const openPreview = async () => {
    setLoadingPreview(true)
    setPreview({ loading: true })
    try {
      const { data } = await hookService.previewPlanningRisks(hookId)
      setPreview(data)
    } catch (error) {
      setPreview(null)
      message.error(t('hooks.planningRisks.previewError', 'Não foi possível montar a prévia.'))
    } finally {
      setLoadingPreview(false)
    }
  }

  const sendNow = async () => {
    setSending(true)
    try {
      const { data } = await hookService.sendPlanningRisks(hookId)
      if (data && data.sended)
        message.success(data.resultMessage || t('hooks.planningRisks.sent', 'E-mail enviado'))
      else
        message.error(
          (data && data.resultMessage) ||
            t('hooks.planningRisks.sendError', 'Não foi possível enviar.')
        )
      if (onSent) onSent()
    } catch (error) {
      message.error(t('hooks.planningRisks.sendError', 'Não foi possível enviar.'))
    } finally {
      setSending(false)
    }
  }

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <Button icon={<EyeOutlined />} onClick={openPreview} loading={loadingPreview}>
        {t('hooks.planningRisks.preview', 'Ver prévia do e-mail')}
      </Button>
      <Popconfirm
        title={t(
          'hooks.planningRisks.sendConfirm',
          'Enviar agora o resumo com todos os riscos para os e-mails do webhook?'
        )}
        okText={t('hooks.planningRisks.send', 'Enviar agora')}
        cancelText={t('hooks.cancel')}
        onConfirm={sendNow}
      >
        <Button type="primary" icon={<SendOutlined />} loading={sending}>
          {t('hooks.planningRisks.send', 'Enviar agora')}
        </Button>
      </Popconfirm>

      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        footer={null}
        width={760}
        title={preview && preview.subject}
      >
        {preview && preview.loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          preview && (
            <>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                {t('hooks.planningRisks.previewTo', 'Para')}:{' '}
                {(preview.recipients || []).join('; ')}
              </div>
              <iframe
                title="preview"
                srcDoc={preview.html}
                sandbox=""
                style={{ width: '100%', height: 620, border: '1px solid #eee', borderRadius: 8 }}
              />
            </>
          )
        )}
      </Modal>
    </div>
  )
}
